



















import { defineComponent, onMounted, PropType, ref, watch } from '@vue/composition-api';
import { ValidationProvider } from 'vee-validate';
import { inputProps, useDisplayLabel } from '../../formUtils';
import { chunk } from 'lodash';
import rules from '../../rules';

rules();

export default defineComponent({
	components: { ValidationProvider },
	props: {
		...inputProps,
		type: {
			required: false,
			default: 'text',
			type: String as PropType<'text' | 'email' | 'password' | 'date'>,
		},
		maxDecimals: {
			type: Number,
			default: 2,
		},
		format: {
			type: Boolean,
			default: false,
		},
	},

	setup(props, { emit, root, listeners }) {
		const inputField = ref<HTMLInputElement | null>(null);
		const { displayLabel } = useDisplayLabel(props);
		const cursorSwap = ref(true);

		const formatValue = (value: string) => {
			let [leftSide, rightSide] = value.split(','); // eslint-disable-line

			const unseparatedLeftSide = leftSide.replaceAll('.', '');

			// Cut off the preceding zero if there is any
			if (unseparatedLeftSide.length > 1 && unseparatedLeftSide.charAt(0) === '0') {
				leftSide = leftSide.slice(1);
			}

			// parseFloat doesn't work because of decimal precision, so we do some string wizardy here
			// to calculate thousand separators.
			const leftSideFormatted = props.format
				? chunk(
						leftSide
							.replaceAll('.', '')
							.split('')
							.reverse(),
						3,
				  )
						.map((c) => c.join(''))
						.join('.')
						.split('')
						.reverse()
						.join('')
				: leftSide;

			let result = null;

			let diff = 0;

			if ((value.match(/,/) || []).length < 1) {
				result = leftSideFormatted;
				diff = result.length - value.length;
				emit('input', result);
			} else {
				rightSide = typeof rightSide !== 'undefined' ? rightSide.slice(0, props.maxDecimals) : '';
				result = leftSideFormatted + ',' + (rightSide ?? '');
				diff = result.length - value.length;
				emit('input', result);
			}

			return diff;
		}

		onMounted(() => {
			if (!props.format) return;
			formatValue(props.value);
		});

		watch(() => props.value, () => {
			if (!props.format || props.value.includes('.')) return;
			formatValue(props.value)
		})

		const handleInput = (e: InputEvent) => {
			let value = (e.target as HTMLInputElement).value;

			// Handle return key
			if (e.data === null) return emit('input', value);
			if (!/[0-9,.]/.test(e.data!)) return;

			if (e.data === '.') {
				value = value.slice(0, -1) + ',';
			}

			if ((e.data === '.' || e.data === ',') && props.value === '') return emit('input', '0,');

			const containsMultipleCommas = (value.match(/,/g) || []).length > 1;

			// We have multiple commas so swap cursor position
			if (containsMultipleCommas) {
				const commaPosition = value.indexOf(',');
				const cursorPosition = cursorSwap.value ? commaPosition + 1 : commaPosition;

				setTimeout(() => {
					inputField.value?.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);

				cursorSwap.value = !cursorSwap.value;
				return;
			}

			const cursorPosition = inputField.value?.selectionStart;
			const diff = formatValue(value);

			setTimeout(() => {
				if (!cursorPosition) return;
				inputField.value?.setSelectionRange(cursorPosition + diff, cursorPosition + diff);
			});
		};

		return { displayLabel, handleInput, inputField };
	},
});
